<template>
  <div class="min-h-screen bg-[#f6f7f9]">

    <!-- ── Hero ── -->
    <section class="bg-white border-b border-[#f0f0f0] py-12 lg:py-16">
      <div class="max-w-screen-xl mx-auto px-6">
        <!-- Breadcrumb -->
        <nav class="flex items-center gap-1.5 text-[0.8rem] text-[#999] mb-6 flex-wrap">
          <router-link to="/" class="hover:text-[#111] transition-colors no-underline"><i class="bi bi-house"></i></router-link>
          <i class="bi bi-chevron-right text-[0.6rem]"></i>
          <span class="text-[#333] font-medium">Installatie</span>
        </nav>

        <div class="flex flex-col lg:flex-row gap-10 items-center">
          <div class="flex-1">
            <span class="inline-flex items-center gap-1.5 text-[0.68rem] font-bold uppercase tracking-[0.1em] text-[#7a5f00] bg-[#FFF3B0] px-3 py-1 rounded-[6px] mb-4">
              <i class="bi bi-tools"></i> Installatiegids
            </span>
            <h1 class="text-[clamp(1.75rem,4vw,2.75rem)] font-black text-[#111] leading-tight mb-4">
              Thuisbatterij installeren: zo doe je dat
            </h1>
            <p class="text-[1.0625rem] text-[#555] leading-relaxed mb-6 max-w-[560px]">
              De meeste plug &amp; play thuisbatterijen sluit je zelf aan in 30 minuten. Geen elektricien, geen bouwkundige ingrepen. Kom je er toch niet uit? Dan helpt Plug2Charge je verder.
            </p>
            <div class="flex flex-wrap gap-3">
              <a href="#stappen" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-5 py-3 rounded-xl no-underline inst-btn-primary text-[0.9375rem]">
                <i class="bi bi-lightning-charge"></i> Bekijk de handleiding
              </a>
              <a href="#service" class="inline-flex items-center gap-2 bg-white hover:bg-[#f8f9fa] border border-[#e5e7eb] text-[#111] font-semibold px-5 py-3 rounded-xl no-underline transition-colors text-[0.9375rem]">
                <i class="bi bi-person-gear"></i> Laat het installeren
              </a>
            </div>
          </div>

          <!-- Stats blokjes -->
          <div class="grid grid-cols-2 gap-3 shrink-0">
            <div class="bg-[#f8f9fa] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">30</div>
              <div class="text-[0.78rem] text-[#888] font-medium">minuten installatie</div>
            </div>
            <div class="bg-[#FFD500] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">0</div>
              <div class="text-[0.78rem] text-[#111]/60 font-medium">bouwkundige ingrepen</div>
            </div>
            <div class="bg-[#f8f9fa] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-[#111] leading-none mb-1">IP65</div>
              <div class="text-[0.78rem] text-[#888] font-medium">weerbestendig</div>
            </div>
            <div class="bg-[#111] rounded-2xl p-5 text-center">
              <div class="text-[2rem] font-black text-white leading-none mb-1">✓</div>
              <div class="text-[0.78rem] text-white/60 font-medium">geen elektricien nodig</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Stap-voor-stap handleiding ── -->
    <section id="stappen" class="py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">

        <div class="text-center mb-12">
          <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-[#111] mb-3">In 5 stappen klaar</h2>
          <p class="text-[#666] text-[1rem] max-w-[480px] mx-auto">Volg deze stappen en je thuisbatterij slaat vandaag al stroom op.</p>
        </div>

        <div class="flex flex-col gap-4 max-w-3xl mx-auto">
          <div
            v-for="(step, i) in steps"
            :key="i"
            class="bg-white rounded-2xl border border-[#e9eaec] overflow-hidden cursor-pointer"
            @click="openStep = openStep === i ? null : i"
          >
            <div class="flex items-center gap-4 px-6 py-5">
              <div class="w-10 h-10 rounded-xl flex items-center justify-center text-[0.9rem] font-black shrink-0"
                :class="openStep === i ? 'bg-[#FFD500] text-[#111]' : 'bg-[#f3f4f6] text-[#888]'">
                {{ i + 1 }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-bold text-[#111] text-[0.9375rem] leading-snug">{{ step.title }}</p>
                <p class="text-[0.8rem] text-[#888] mt-0.5">{{ step.sub }}</p>
              </div>
              <i :class="['bi text-[#aaa] text-[1rem] transition-transform shrink-0',
                openStep === i ? 'bi-chevron-up text-[#FFD500]' : 'bi-chevron-down']"></i>
            </div>

            <transition name="accordion">
              <div v-if="openStep === i" class="border-t border-[#f3f4f6]">
                <div class="px-6 py-5 flex flex-col gap-5">
                  <div>
                    <p class="text-[0.9rem] text-[#444] leading-relaxed mb-3" v-html="step.body"></p>
                    <ul v-if="step.tips" class="flex flex-col gap-1.5">
                      <li v-for="tip in step.tips" :key="tip"
                        class="flex items-start gap-2 text-[0.85rem] text-[#555]">
                        <i class="bi bi-check2-circle text-[#FFD500] mt-0.5 shrink-0"></i>
                        {{ tip }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="step.warning" class="bg-[#fffbe6] border border-[#FFD500]/40 rounded-xl p-4 min-w-[220px]">
                    <p class="text-[0.78rem] font-bold text-[#7a5f00] mb-1 flex items-center gap-1.5">
                      <i class="bi bi-exclamation-triangle"></i> Let op
                    </p>
                    <p class="text-[0.78rem] text-[#7a5f00]">{{ step.warning }}</p>
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Veelgestelde vragen ── -->
    <section class="bg-white border-t border-b border-[#f0f0f0] py-14">
      <div class="max-w-3xl mx-auto px-6">
        <h2 class="text-[1.5rem] font-black text-[#111] mb-8 text-center">Veelgestelde vragen</h2>
        <div class="flex flex-col divide-y divide-[#f3f4f6]">
          <div v-for="(faq, i) in faqs" :key="i" class="py-4 cursor-pointer" @click="openFaq = openFaq === i ? null : i">
            <div class="flex items-center justify-between gap-4">
              <p class="font-semibold text-[0.9375rem] text-[#111]">{{ faq.q }}</p>
              <i :class="['bi shrink-0 text-[#aaa]', openFaq === i ? 'bi-dash' : 'bi-plus']"></i>
            </div>
            <transition name="accordion">
              <p v-if="openFaq === i" class="text-[0.875rem] text-[#555] leading-relaxed mt-3 pr-8">{{ faq.a }}</p>
            </transition>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Installatieservice funnel ── -->
    <section id="service" class="py-14 lg:py-20">
      <div class="max-w-screen-xl mx-auto px-6">

        <div class="bg-[#111] rounded-3xl overflow-hidden">
          <div class="grid lg:grid-cols-2 gap-0">

            <!-- Links: uitleg -->
            <div class="p-8 lg:p-12 flex flex-col justify-center">
              <span class="inline-flex items-center gap-1.5 text-[0.72rem] font-bold uppercase tracking-widest text-[#FFD500] mb-4">
                <i class="bi bi-person-gear"></i> Professionele installatieservice
              </span>
              <h2 class="text-[clamp(1.5rem,3vw,2.25rem)] font-black text-white leading-tight mb-4">
                Kom je er niet uit? Wij installeren het voor je.
              </h2>
              <p class="text-white/60 text-[0.9375rem] leading-relaxed mb-8">
                Onze gecertificeerde installateurs regelen alles: van plaatsing en aansluiting van de thuisbatterij tot koppeling met de app en eerste configuratie. Jij hoeft er niets aan te doen.
              </p>
              <ul class="flex flex-col gap-3">
                <li v-for="usp in serviceUsps" :key="usp" class="flex items-center gap-3 text-[0.875rem] text-white/80">
                  <div class="w-6 h-6 rounded-full bg-[#FFD500] flex items-center justify-center shrink-0">
                    <i class="bi bi-check text-[#111] text-[0.7rem] font-black"></i>
                  </div>
                  {{ usp }}
                </li>
              </ul>
            </div>

            <!-- Rechts: funnel form -->
            <div class="bg-white p-8 lg:p-10 flex flex-col">

              <!-- Stap indicators -->
              <div class="flex items-center gap-0 mb-8">
                <template v-for="(label, i) in funnelStepLabels" :key="i">
                  <div class="flex items-center gap-1.5">
                    <div class="w-6 h-6 rounded-full text-[0.7rem] font-black flex items-center justify-center shrink-0 inst-step-dot"
                      :class="funnelStep > i ? 'bg-[#FFD500] text-[#111]' : funnelStep === i ? 'bg-[#111] text-white' : 'bg-[#f3f4f6] text-[#aaa]'">
                      <i v-if="funnelStep > i" class="bi bi-check text-[0.6rem]"></i>
                      <span v-else>{{ i + 1 }}</span>
                    </div>
                    <span class="text-[0.72rem] font-semibold hidden sm:inline"
                      :class="funnelStep === i ? 'text-[#111]' : 'text-[#aaa]'">{{ label }}</span>
                  </div>
                  <div v-if="i < funnelStepLabels.length - 1" class="flex-1 h-px mx-2 transition-colors"
                    :class="funnelStep > i ? 'bg-[#FFD500]' : 'bg-[#f3f4f6]'"></div>
                </template>
              </div>

              <!-- Stap 0: Wat wil je installeren? -->
              <div v-if="funnelStep === 0" class="flex flex-col gap-3 flex-1">
                <h3 class="font-bold text-[#111] text-[1.0625rem] mb-1">Wat wil je laten installeren?</h3>
                <label v-for="opt in installTypes" :key="opt.value"
                  @click="form.type = opt.value"
                  :class="['flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer inst-option-card',
                    form.type === opt.value ? 'border-[#FFD500] bg-[#fffbe6]' : 'border-[#e5e7eb] hover:border-[#FFD500]/50']">
                  <div class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    :class="form.type === opt.value ? 'bg-[#FFD500] text-[#111]' : 'bg-[#f8f9fa] text-[#aaa]'">
                    <i :class="['bi text-[1.1rem]', opt.icon]"></i>
                  </div>
                  <div class="flex-1">
                    <p class="font-semibold text-[#111] text-[0.875rem]">{{ opt.label }}</p>
                    <p class="text-[0.775rem] text-[#888]">{{ opt.sub }}</p>
                  </div>
                  <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                    :class="form.type === opt.value ? 'border-[#FFD500] bg-[#FFD500]' : 'border-[#ddd]'">
                    <i v-if="form.type === opt.value" class="bi bi-check text-[0.6rem] text-[#111] font-black"></i>
                  </div>
                </label>
                <button @click="funnelStep = 1" :disabled="!form.type"
                  class="mt-auto w-full bg-[#FFD500] hover:bg-[#ffb800] disabled:opacity-40 disabled:cursor-not-allowed text-[#111] font-bold py-3 rounded-xl inst-btn-primary">
                  Volgende stap <i class="bi bi-arrow-right ml-1"></i>
                </button>
              </div>

              <!-- Stap 1: Situatie -->
              <div v-if="funnelStep === 1" class="flex flex-col gap-3 flex-1">
                <h3 class="font-bold text-[#111] text-[1.0625rem] mb-1">Wat is jouw situatie?</h3>
                <label v-for="opt in situationTypes" :key="opt.value"
                  @click="form.situation = opt.value"
                  :class="['flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer inst-option-card',
                    form.situation === opt.value ? 'border-[#FFD500] bg-[#fffbe6]' : 'border-[#e5e7eb] hover:border-[#FFD500]/50']">
                  <div class="w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0"
                    :class="form.situation === opt.value ? 'border-[#FFD500] bg-[#FFD500]' : 'border-[#ddd]'">
                    <i v-if="form.situation === opt.value" class="bi bi-check text-[0.6rem] text-[#111]"></i>
                  </div>
                  <div>
                    <p class="font-semibold text-[#111] text-[0.875rem]">{{ opt.label }}</p>
                    <p class="text-[0.775rem] text-[#888]">{{ opt.sub }}</p>
                  </div>
                </label>
                <div class="flex gap-3 mt-auto">
                  <button @click="funnelStep = 0" class="px-4 py-3 rounded-xl border border-[#e5e7eb] text-[#666] font-semibold hover:border-[#ddd] transition-colors text-[0.875rem]">
                    <i class="bi bi-arrow-left"></i>
                  </button>
                  <button @click="funnelStep = 2" :disabled="!form.situation"
                    class="flex-1 bg-[#FFD500] hover:bg-[#ffb800] disabled:opacity-40 disabled:cursor-not-allowed text-[#111] font-bold py-3 rounded-xl inst-btn-primary">
                    Volgende <i class="bi bi-arrow-right ml-1"></i>
                  </button>
                </div>
              </div>

              <!-- Stap 2: Contactgegevens -->
              <div v-if="funnelStep === 2" class="flex flex-col gap-4 flex-1">
                <input v-model="hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true" class="hidden" />
                <h3 class="font-bold text-[#111] text-[1.0625rem] mb-1">Jouw gegevens</h3>
                <div class="grid grid-cols-2 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-[0.78rem] font-semibold text-[#555]">Voornaam *</label>
                    <input v-model="form.name" type="text" placeholder="Thomas" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors">
                  </div>
                  <div class="flex flex-col gap-1">
                    <label class="text-[0.78rem] font-semibold text-[#555]">Postcode *</label>
                    <input v-model="form.postcode" type="text" placeholder="1234 AB" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors">
                  </div>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[0.78rem] font-semibold text-[#555]">E-mailadres *</label>
                  <input v-model="form.email" type="email" placeholder="thomas@email.nl" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors">
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[0.78rem] font-semibold text-[#555]">Telefoon</label>
                  <input v-model="form.phone" type="tel" placeholder="+31 6 12345678" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors">
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-[0.78rem] font-semibold text-[#555]">Opmerking (optioneel)</label>
                  <textarea v-model="form.note" rows="3" placeholder="Bijv. type batterij, capaciteit, situatie thuis…" class="border border-[#e5e7eb] rounded-lg px-3 py-2.5 text-[0.875rem] outline-none focus:border-[#FFD500] transition-colors resize-none"></textarea>
                </div>
                <p v-if="submitError" class="flex items-start gap-2 text-[0.82rem] text-[#b42318] bg-[#fef3f2] border border-[#fecdca] rounded-lg px-3 py-2.5" role="alert">
                  <i class="bi bi-exclamation-triangle-fill mt-0.5"></i>
                  <span>{{ submitError }}</span>
                </p>
                <div class="flex gap-3 mt-auto">
                  <button @click="funnelStep = 1" class="px-4 py-3 rounded-xl border border-[#e5e7eb] text-[#666] font-semibold hover:border-[#ddd] transition-colors text-[0.875rem]">
                    <i class="bi bi-arrow-left"></i>
                  </button>
                  <button @click="submitFunnel" :disabled="!canSubmit || submitting"
                    class="flex-1 bg-[#FFD500] hover:bg-[#ffb800] disabled:opacity-40 disabled:cursor-not-allowed text-[#111] font-bold py-3 rounded-xl inst-btn-primary flex items-center justify-center gap-2">
                    <i :class="['bi', submitting ? 'bi-arrow-repeat' : 'bi-send']"></i>
                    {{ submitting ? 'Verzenden…' : (submitError ? 'Opnieuw proberen' : 'Aanvraag versturen') }}
                  </button>
                </div>
              </div>

              <!-- Bevestiging -->
              <div v-if="funnelStep === 3" class="flex flex-col items-center justify-center text-center flex-1 py-4">
                <div class="w-16 h-16 rounded-2xl bg-[#FFD500] flex items-center justify-center mb-5">
                  <i class="bi bi-check-lg text-[2rem] text-[#111] font-black"></i>
                </div>
                <h3 class="text-[1.25rem] font-black text-[#111] mb-2">Aanvraag ontvangen!</h3>
                <p class="text-[0.9rem] text-[#666] leading-relaxed max-w-[320px]">
                  We nemen binnen <strong>1 werkdag</strong> contact met je op om een afspraak in te plannen.
                </p>
                <p class="text-[0.8rem] text-[#aaa] mt-3">Controleer ook je spammap.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// ── Stap-voor-stap handleiding ────────────────────────────────────────────────
const openStep = ref(0)

const steps = [
  {
    title: 'Kies een geschikte locatie',
    sub:   'Droog, geventileerd en bereikbaar',
    body:  'Plaats de thuisbatterij op een droge, goed geventileerde plek: garage, bijkeuken of technische ruimte. De meeste modellen zijn ook buitengeschikt (IP65/IP66).',
    tips:  [
      'Houd minimaal 20 cm vrije ruimte aan alle kanten voor warmteafvoer',
      'Vermijd directe blootstelling aan langdurig zonlicht: hoge temperaturen verminderen de levensduur',
      'Controleer of de vloer het gewicht aankan (20–50 kg afhankelijk van model)',
    ],
  },
  {
    title: 'Sluit de netkabel aan op het stopcontact',
    sub:   'Gewone Schuko-stekker, geen elektricien nodig',
    body:  'Plug & play thuisbatterijen (zoals de Hoymiles HiBattery) hebben een gewone Schuko-stekker. Steek de stekker in een geaard stopcontact op een aparte groep van minimaal 16A.',
    tips:  [
      'Gebruik een aparte groep voor de batterij zodat andere apparaten niet beïnvloed worden',
      'Controleer of het stopcontact geaard is (3-polig)',
      'Gebruik geen verlengsnoer of stekkerdoos',
    ],
    warning: 'Bij twijfel over de groepszekering: laat het stopcontact controleren door een elektricien.',
  },
  {
    title: 'Download de app en maak een account',
    sub:   'iOS of Android, ±3 minuten',
    body:  'Download de bijbehorende app (bijv. Hoymiles S-Miles Cloud of Marstek app) via de App Store of Google Play. Maak een gratis account aan met je e-mailadres.',
    tips:  [
      'Zorg dat je smartphone verbonden is met je thuisnetwerk (WiFi 2,4 GHz)',
      'Houd het serienummer van de batterij bij de hand (staat op het apparaat en in de doos)',
    ],
  },
  {
    title: 'Koppel de batterij aan het WiFi-netwerk',
    sub:   'Via QR-code of handmatig serienummer',
    body:  'Scan de QR-code op de batterij of voer het serienummer in de app in. De app begeleidt je door de WiFi-koppeling. Na verbinding zie je direct de status, laadstand en instellingen.',
    tips:  [
      'De batterij maakt tijdelijk een eigen hotspot aan voor de koppeling',
      'Gebruik 2,4 GHz WiFi (de meeste thuisbatterijen ondersteunen geen 5 GHz)',
    ],
  },
  {
    title: 'Stel laad- en ontlaadtijden in',
    sub:   'Automatisch of handmatig via de app',
    body:  'Configureer wanneer de batterij oplaadt (bijv. overdag bij zonnestroom of bij goedkope stroomtarieven) en wanneer hij stroom levert (bijv. avondspits). De meeste modellen ondersteunen koppeling met een dynamisch energiecontract.',
    tips:  [
      'Stel "zelfverbruik" in als je zonnepanelen hebt: de batterij laadt dan automatisch op bij overschot',
      'Gebruik de tijdschema\'s voor optimaal gebruik bij een dynamisch energiecontract',
      'Monitor de eerste week het verbruik via de app om de instellingen te fijn-tunen',
    ],
  },
]

// ── FAQ ───────────────────────────────────────────────────────────────────────
const openFaq = ref(null)

const faqs = [
  {
    q: 'Heb ik een elektricien nodig voor een thuisbatterij?',
    a: 'Voor plug & play thuisbatterijen (zoals de Hoymiles HiBattery) heb je geen elektricien nodig. Je sluit ze aan op een gewoon geaard stopcontact. Grotere systeembatterijen die direct op de meterkast worden aangesloten, vereisen wel een gecertificeerd installateur.',
  },
  {
    q: 'Welke capaciteit past bij mij?',
    a: 'Een gemiddeld huishouden verbruikt \'s avonds en \'s nachts 5–10 kWh. Een batterij van 5 kWh dekt dan een groot deel van je avondverbruik. Heb je zonnepanelen? Kies dan een capaciteit die overdag het overschot volledig opvangt, reken op ±1 kWh per 300 Wp paneelvermogen.',
  },
  {
    q: 'Wat is het verschil tussen plug & play en een systeembatterij?',
    a: 'Een plug & play thuisbatterij (bijv. 1,92–5 kWh) sluit je aan als een gewoon apparaat. Een systeembatterij (5–20+ kWh) wordt direct op de meterkast of omvormer gekoppeld en vereist installatie door een elektricien. Plug & play is goedkoper en sneller; een systeembatterij biedt meer capaciteit en integratie.',
  },
  {
    q: 'Hoeveel bespaar ik met een thuisbatterij?',
    a: 'Dat hangt af van je verbruiksprofiel en energiecontract. Met een dynamisch contract en slimme laadtijden (goedkope uren) bespaar je gemiddeld €150–€400 per jaar. Bij combinatie met zonnepanelen kan de besparing hoger uitvallen doordat je meer zelfopgewekte stroom benut.',
  },
  {
    q: 'Is een thuisbatterij veilig?',
    a: 'Ja. Onze thuisbatterijen voldoen aan CE-certificering en gebruiken LFP-chemie (lithiumijzerfosfaat), de veiligste Li-ionvariant. LFP-batterijen zijn thermisch stabiel, niet brandgevaarlijk bij overbelasting, en hebben een levensduur van 3.000–6.000 laadcycli.',
  },
]

// ── Installatieservice funnel ─────────────────────────────────────────────────
const funnelStep       = ref(0)
const funnelStepLabels = ['Type', 'Situatie', 'Gegevens']
const submitting       = ref(false)

const form = ref({
  type:      '',
  situation: '',
  name:      '',
  postcode:  '',
  email:     '',
  phone:     '',
  note:      '',
})

const canSubmit = computed(() =>
  form.value.name.trim() &&
  form.value.postcode.trim() &&
  form.value.email.includes('@')
)

const installTypes = [
  { value: 'plug-play',  icon: 'bi-plug-fill',          label: 'Plug & Play batterij',  sub: 'Aansluiten op stopcontact, zonder elektricien' },
  { value: 'systeem',    icon: 'bi-battery-charging',   label: 'Systeembatterij',        sub: 'Koppeling aan meterkast of omvormer' },
  { value: 'combi',      icon: 'bi-sun',                label: 'Zonnepanelen + batterij', sub: 'Complete energieopslagoplossing' },
  { value: 'overig',     icon: 'bi-three-dots',         label: 'Iets anders',            sub: 'Advies over mijn specifieke situatie' },
]

const situationTypes = [
  { value: 'materiaal', label: 'Ik heb al materiaal van Plug2Charge',  sub: 'Ik heb een set gekocht en wil hulp bij installatie' },
  { value: 'advies',    label: 'Ik wil eerst advies en een offerte',   sub: 'Ik weet nog niet precies wat ik nodig heb' },
  { value: 'compleet',  label: 'Ik wil alles in één keer regelen',     sub: 'Materiaal + installatie als compleet pakket' },
]

const serviceUsps = [
  'Gecertificeerde installateurs met ervaring',
  'Inclusief aansluiting en app-configuratie',
  'Volledige garantie op installatie',
  'Afspraak binnen 5 werkdagen',
  'Achteraf betalen via factuur',
]

const submitError = ref('')
const hp = ref('')   // honeypot

const submitFunnel = async () => {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true
  submitError.value = ''

  try {
    const res = await fetch('/api/installation-request', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ ...form.value, website: hp.value }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Versturen mislukt. Probeer het opnieuw.')
    }
    funnelStep.value = 3   // bevestigingsstap
  } catch (e) {
    submitError.value = e.message || 'Er ging iets mis. Controleer je verbinding en probeer het opnieuw.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.inst-step-dot {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              color 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.inst-option-card {
  transition: border-color 150ms cubic-bezier(0.23, 1, 0.32, 1),
              background 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.inst-btn-primary {
  transition: background 150ms cubic-bezier(0.23, 1, 0.32, 1),
              transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
}
.inst-btn-primary:not(:disabled):hover  { transform: translateY(-1px); }
.inst-btn-primary:not(:disabled):active { transform: scale(0.98); }
@media (prefers-reduced-motion: reduce) {
  .inst-step-dot, .inst-option-card, .inst-btn-primary { transition: none; }
}

.accordion-enter-active,
.accordion-leave-active { transition: height 220ms cubic-bezier(0.23,1,0.32,1), opacity 220ms cubic-bezier(0.23,1,0.32,1); overflow: hidden; }
.accordion-enter-from,
.accordion-leave-to { opacity: 0; max-height: 0; }
.accordion-enter-to,
.accordion-leave-from { opacity: 1; max-height: 600px; }
</style>
